// frontend/src/pages/RankingsPage.js
import React, { useEffect, useState, useRef, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import api from '../api'
import Navbar from '../components/Navbar'
import { useCurrentBasho } from '../hooks/useCurrentBasho'

const font      = "'Noto Serif JP', Georgia, serif"
const ink       = '#1A0A00'
const muted     = '#8A7A5A'
const accent    = '#C41E3A'
const cardBorder = '#D4C8A8'
const parchment = '#F5F0E8'

const MONTH_NAMES_R = { '01': 'January', '03': 'March', '05': 'May', '07': 'July', '09': 'September', '11': 'November' }

function bashoDisplayName(id) {
  if (!id) return 'current basho'
  const mm = id.slice(4, 6)
  return `${MONTH_NAMES_R[mm] || mm} ${id.slice(0, 4)}`
}

function fmtRank(rank) {
  if (!rank) return '—'
  const side = /east/i.test(rank) ? 'E' : 'W'
  const num  = (rank.match(/\d+/) || [''])[0]
  if (/yokozuna/i.test(rank))   return `Y${num}${side}`
  if (/ozeki/i.test(rank))      return `O${num}${side}`
  if (/sekiwake/i.test(rank))   return `S${num}${side}`
  if (/komusubi/i.test(rank))   return `K${num}${side}`
  if (/maegashira/i.test(rank)) return `M${num}${side}`
  if (/juryo/i.test(rank))      return `J${num}${side}`
  return rank.replace(/\s+(East|West)$/i, '').trim()
}

// ── Drag handle icon ──────────────────────────────────────────────────────────
function DragHandle({ listeners, attributes, active }) {
  return (
    <div
      {...(active ? listeners : {})}
      {...(active ? attributes : {})}
      style={{
        width: 28, flexShrink: 0, display: 'flex', alignItems: 'center',
        justifyContent: 'center', cursor: active ? 'grab' : 'default',
        color: active ? '#B4A880' : '#D4C8A8',
        touchAction: 'none',
        userSelect: 'none',
        fontSize: 14,
      }}
      title={active ? 'Drag to reorder' : undefined}
    >
      ⋮⋮
    </div>
  )
}

// ── Sortable row ──────────────────────────────────────────────────────────────
function SortableRow({ wrestler, idx, isCustomMode }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: wrestler.name, disabled: !isCustomMode })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    zIndex: isDragging ? 999 : 'auto',
    position: isDragging ? 'relative' : undefined,
  }

  return (
    <div ref={setNodeRef} style={{ ...s.row, ...style }}>
      <DragHandle listeners={listeners} attributes={attributes} active={isCustomMode} />
      <div style={s.posNum}>{idx + 1}</div>
      <div style={s.rankCol} className="rankings-row-rank">{fmtRank(wrestler.rank)}</div>
      <div style={s.nameCol}>{wrestler.name}</div>
      <div style={s.metaCol} className="rankings-row-meta">
        {wrestler.prevRank && (
          <span style={s.metaItem}>{fmtRank(wrestler.prevRank)}</span>
        )}
        {wrestler.prevWins != null && wrestler.prevLosses != null && (
          <span style={s.metaItem}>{wrestler.prevWins}–{wrestler.prevLosses}</span>
        )}
      </div>
    </div>
  )
}

// ── Static row (read-only banzuke view) ──────────────────────────────────────
function StaticRow({ wrestler, idx }) {
  return (
    <div style={s.row}>
      <DragHandle active={false} />
      <div style={s.posNum}>{idx + 1}</div>
      <div style={s.rankCol} className="rankings-row-rank">{fmtRank(wrestler.rank)}</div>
      <div style={s.nameCol}>{wrestler.name}</div>
      <div style={s.metaCol} className="rankings-row-meta">
        {wrestler.prevRank && (
          <span style={s.metaItem}>{fmtRank(wrestler.prevRank)}</span>
        )}
        {wrestler.prevWins != null && wrestler.prevLosses != null && (
          <span style={s.metaItem}>{wrestler.prevWins}–{wrestler.prevLosses}</span>
        )}
      </div>
    </div>
  )
}

// ── Radio option ──────────────────────────────────────────────────────────────
function RadioOption({ value, label, desc, checked, onChange }) {
  return (
    <div style={s.radioOption} onClick={() => onChange(value)}>
      <div style={s.radioCircle(checked)} />
      <div>
        <div style={s.radioLabel}>{label}</div>
        <div style={s.radioDesc}>{desc}</div>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function RankingsPage() {
  const location = useLocation()
  const { basho: currentBasho, loading: bashoLoading } = useCurrentBasho()

  // ?basho=XXXXXX param: view a past basho's rankings in read-only mode
  const searchParams = new URLSearchParams(location.search)
  const pastBashoParam = searchParams.get('basho')
  const basho = pastBashoParam || currentBasho
  const isPastBasho = !!pastBashoParam

  const [banzuke, setBanzuke]             = useState([])
  const [banzukeAvailable, setBanzukeAvailable] = useState(false)
  const [strategy, setStrategy]           = useState(null)   // null = not set yet
  const [customList, setCustomList]       = useState([])     // displayed order
  const [dbHasCustom, setDbHasCustom]     = useState(false)  // true once saved to DB
  const [loading, setLoading]             = useState(true)
  const [saveStatus, setSaveStatus]       = useState(null)   // null | 'saving' | 'saved' | 'failed'
  const [showResetConfirm, setShowResetConfirm] = useState(false)

  const saveTimerRef   = useRef(null)
  const debounceRef    = useRef(null)
  const pendingListRef = useRef(null)

  // ── dnd-kit sensors ──────────────────────────────────────────────────────
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor,   { activationConstraint: { delay: 200, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  // ── Helpers ───────────────────────────────────────────────────────────────
  const showSaved = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    setSaveStatus('saved')
    saveTimerRef.current = setTimeout(() => setSaveStatus(null), 2500)
  }, [])

  const saveCustomRankings = useCallback(async (list) => {
    const payload = list.map((w, i) => ({ rikishiName: w.name, position: i + 1 }))
    await api.post(`/rankings/custom/${basho}`, { rankings: payload })
  }, [basho])

  // ── Initial data load ──────────────────────────────────────────────────────
  useEffect(() => {
    // For past basho, basho is known immediately from query param; don't wait for current basho detection
    if (!basho || (bashoLoading && !isPastBasho)) return
    setLoading(true)

    Promise.all([
      api.get(`/rankings/banzuke/${basho}`).catch(() => ({ data: { wrestlers: [], available: false } })),
      api.get(`/autofill/${basho}`).catch(() => ({ data: { strategy: null } })),
      api.get(`/rankings/custom/${basho}`).catch(() => ({ data: { rankings: [] } })),
    ])
      .then(([bzRes, prefRes, customRes]) => {
        const { wrestlers = [], available = false } = bzRes.data
        setBanzuke(wrestlers)
        setBanzukeAvailable(available)

        const strat = prefRes.data.strategy || 'BY_RANK'
        setStrategy(strat)

        const savedCustom = customRes.data.rankings || []
        setDbHasCustom(savedCustom.length > 0)

        if (savedCustom.length > 0) {
          // Restore saved order, then append any new wrestlers not yet in the custom list
          const wMap = new Map(wrestlers.map(w => [w.name, w]))
          const ordered = savedCustom
            .sort((a, b) => a.position - b.position)
            .map(r => wMap.get(r.rikishiName) || { name: r.rikishiName, rank: null, prevRank: null, prevWins: null, prevLosses: null })
          const inCustom = new Set(savedCustom.map(r => r.rikishiName))
          const newOnes  = wrestlers.filter(w => !inCustom.has(w.name))
          setCustomList([...ordered, ...newOnes])
        } else {
          // Default: banzuke order (not yet saved to DB)
          setCustomList(wrestlers)
        }
      })
      .finally(() => setLoading(false))
  }, [basho, bashoLoading])

  // ── Strategy change (auto-saves immediately) ───────────────────────────────
  const handleStrategyChange = async (newStrategy) => {
    if (isPastBasho || newStrategy === strategy) return
    const prev = strategy
    setStrategy(newStrategy)
    setSaveStatus('saving')

    try {
      // If switching to CUSTOM for the first time, persist the banzuke order as baseline
      if (newStrategy === 'CUSTOM' && !dbHasCustom) {
        await saveCustomRankings(customList)
        setDbHasCustom(true)
      }
      await api.post(`/autofill/${basho}`, { strategy: newStrategy })
      showSaved()
    } catch {
      setStrategy(prev)
      setSaveStatus('failed')
    }
  }

  // ── Drag end ───────────────────────────────────────────────────────────────
  const handleDragEnd = (event) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIdx = customList.findIndex(w => w.name === active.id)
    const newIdx = customList.findIndex(w => w.name === over.id)
    const reordered = arrayMove(customList, oldIdx, newIdx)
    setCustomList(reordered)
    pendingListRef.current = reordered

    if (debounceRef.current) clearTimeout(debounceRef.current)
    setSaveStatus('saving')
    debounceRef.current = setTimeout(async () => {
      try {
        await saveCustomRankings(pendingListRef.current)
        setDbHasCustom(true)
        showSaved()
      } catch {
        setSaveStatus('failed')
      }
    }, 600)
  }

  // ── Reset to banzuke order ─────────────────────────────────────────────────
  const handleResetClick = () => setShowResetConfirm(true)

  const handleResetConfirm = async () => {
    setShowResetConfirm(false)
    setCustomList(banzuke)
    pendingListRef.current = banzuke
    setSaveStatus('saving')
    try {
      await saveCustomRankings(banzuke)
      setDbHasCustom(true)
      showSaved()
    } catch {
      setSaveStatus('failed')
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  const isCustomMode = strategy === 'CUSTOM'
  const displayList  = isCustomMode ? customList : banzuke
  const listTitle    = isCustomMode
    ? (isPastBasho ? 'Your custom ranking for this basho' : 'Your custom ranking — drag to reorder')
    : 'Official banzuke'

  return (
    <div style={s.page}>
      <style>{`
        @media (max-width: 768px) {
          .rankings-container { padding: 16px 12px 48px !important; }
          .rankings-card      { padding: 18px 14px !important; }
        }
      `}</style>
      <Navbar currentPage="rankings" />

      <div className="rankings-container" style={s.container}>

        {/* ── Page header ───────────────────────────────────────────────── */}
        <div style={s.pageHeader}>
          <div style={s.redLine} />
          <h1 style={s.pageTitle}>Rankings</h1>
          <p style={s.pageSubtitle}>
            {bashoDisplayName(basho)} banzuke
            {isPastBasho && <span style={s.pastLabel}> · past basho</span>}
          </p>
        </div>

        {loading ? (
          <div style={s.loadingState}>Loading…</div>
        ) : (
          <>
            {/* ── Strategy selector card ──────────────────────────────── */}
            {!isPastBasho ? (
              <div className="rankings-card" style={s.card}>
                <div style={s.cardHeader}>
                  <div style={s.redLine} />
                  <h2 style={s.cardTitle}>Auto-fill strategy for {bashoDisplayName(basho)}</h2>
                </div>
                <p style={s.cardDesc}>
                  If you forget to submit picks before matches start, we'll fill them in for you using your chosen strategy.
                </p>

                <RadioOption
                  value="BY_RANK"
                  label="By rank"
                  desc="Always pick the higher-ranked wrestler"
                  checked={strategy === 'BY_RANK'}
                  onChange={handleStrategyChange}
                />
                <RadioOption
                  value="RANDOM"
                  label="Random"
                  desc="Coin flip for each bout"
                  checked={strategy === 'RANDOM'}
                  onChange={handleStrategyChange}
                />
                {banzukeAvailable && (
                  <RadioOption
                    value="CUSTOM"
                    label="Custom ranking"
                    desc="Use your custom ranking below"
                    checked={strategy === 'CUSTOM'}
                    onChange={handleStrategyChange}
                  />
                )}

                <div style={s.saveRow}>
                  {saveStatus === 'saving' && <span style={s.saveIndicator('saving')}>Saving…</span>}
                  {saveStatus === 'saved'  && <span style={s.saveIndicator('saved')}>Saved ✓</span>}
                  {saveStatus === 'failed' && <span style={s.saveIndicator('failed')}>Save failed</span>}
                </div>
              </div>
            ) : (
              <div className="rankings-card" style={{ ...s.card, ...s.pastReadOnlyCard }}>
                <div style={s.pastReadOnlyIcon}>🗓</div>
                <p style={s.pastReadOnlyText}>
                  This is the official banzuke for {bashoDisplayName(basho)}.
                  Strategy settings are only available for the current basho.
                </p>
              </div>
            )}

            {/* ── Banzuke not available ──────────────────────────────── */}
            {!banzukeAvailable && (
              <div className="rankings-card" style={{ ...s.card, ...s.unavailableCard }}>
                <div style={s.unavailableIcon}>⛩️</div>
                <p style={s.unavailableText}>
                  {isPastBasho
                    ? 'The banzuke for this basho is not available.'
                    : `The ${bashoDisplayName(basho)} banzuke isn't showing right now — it may not be published yet, or our data provider is temporarily updating. Check back soon.`}
                </p>
              </div>
            )}

            {/* ── Banzuke / custom ranking list ─────────────────────── */}
            {banzukeAvailable && (
              <div className="rankings-card" style={s.card}>
                <div style={s.cardHeader}>
                  <div style={s.redLine} />
                  <h2 style={s.cardTitle}>{listTitle}</h2>
                </div>

                {/* Column headers */}
                <div style={s.colHeaderRow}>
                  <div style={{ width: 28, flexShrink: 0 }} />
                  <div style={s.colH}>#</div>
                  <div style={{ ...s.colH, flex: '0 0 50px' }} className="rankings-row-rank">Rank</div>
                  <div style={{ ...s.colH, flex: 1 }}>Wrestler</div>
                  <div style={{ ...s.colH, flex: '0 0 75px', textAlign: 'right' }} className="rankings-row-meta">Previous Rank</div>
                </div>
                <div style={s.listDivider} />

                {/* List — draggable only in custom mode for current basho */}
                {isCustomMode && !isPastBasho ? (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={customList.map(w => w.name)}
                      strategy={verticalListSortingStrategy}
                    >
                      {customList.map((w, i) => (
                        <SortableRow
                          key={w.name}
                          wrestler={w}
                          idx={i}
                          isCustomMode
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                ) : (
                  displayList.map((w, i) => (
                    <StaticRow key={w.name} wrestler={w} idx={i} />
                  ))
                )}

                {/* Reset button — only in custom mode for current basho */}
                {isCustomMode && !isPastBasho && (
                  <div style={s.resetArea}>
                    {showResetConfirm ? (
                      <div style={s.resetConfirm}>
                        <p style={s.resetConfirmText}>
                          Reset your custom ranking? This will restore the official banzuke order.
                        </p>
                        <div style={s.resetConfirmBtns}>
                          <button onClick={handleResetConfirm} style={s.confirmBtn}>Confirm</button>
                          <button onClick={() => setShowResetConfirm(false)} style={s.cancelBtn}>Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <button onClick={handleResetClick} style={s.resetBtn}>
                        Reset to banzuke order
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

// ── Styles ────────────────────────────────────────────────────────────────────
const s = {
  page: {
    minHeight: '100vh',
    background: parchment,
    backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(180,160,120,0.06) 3px, rgba(180,160,120,0.06) 4px)',
    color: ink,
  },
  container: {
    maxWidth: 720, margin: '0 auto', padding: '28px 20px 60px',
    display: 'flex', flexDirection: 'column', gap: 20,
  },
  loadingState: { textAlign: 'center', padding: 80, color: muted, fontFamily: font },

  // Page header
  pageHeader: { marginBottom: 4 },
  redLine: { width: 28, height: 3, background: accent, marginBottom: 8 },
  pageTitle: { fontSize: 28, fontWeight: 900, color: ink, margin: 0, fontFamily: font },
  pageSubtitle: { fontSize: 12, color: muted, fontFamily: font, letterSpacing: 2, textTransform: 'uppercase', margin: '4px 0 0' },
  pastLabel: { fontSize: 11, color: '#C41E3A', letterSpacing: 1, textTransform: 'none', fontStyle: 'italic' },

  // Cards
  card: {
    background: '#FFFFFF', border: `1px solid ${cardBorder}`,
    borderRadius: 4, padding: '22px 24px',
  },
  cardHeader: { marginBottom: 14 },
  cardTitle: { fontSize: 15, fontWeight: 700, color: ink, margin: 0, fontFamily: font },
  cardDesc: { fontSize: 12, color: muted, fontFamily: 'Georgia, serif', margin: '0 0 16px', lineHeight: 1.6 },

  // Radio options
  radioOption: {
    display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12, cursor: 'pointer',
  },
  radioCircle: (checked) => ({
    width: 14, height: 14, borderRadius: '50%', flexShrink: 0,
    marginTop: 3, boxSizing: 'border-box',
    border: `2px solid ${checked ? ink : cardBorder}`,
    background: checked ? ink : 'transparent',
    boxShadow: checked ? 'inset 0 0 0 3px white' : 'none',
    transition: 'all 0.1s',
  }),
  radioLabel: { fontSize: 13, fontWeight: 600, color: ink, fontFamily: font, lineHeight: 1.3 },
  radioDesc:  { fontSize: 11, color: muted, fontFamily: 'Georgia, serif', marginTop: 1 },

  // Save indicator
  saveRow: { marginTop: 12, minHeight: 18 },
  saveIndicator: (state) => ({
    fontSize: 11, fontFamily: 'Georgia, serif', fontStyle: 'italic',
    color: state === 'saved' ? '#1A6A1A' : state === 'failed' ? accent : muted,
  }),

  // Unavailable / past basho read-only state
  unavailableCard: { textAlign: 'center', padding: '32px 24px' },
  unavailableIcon: { fontSize: 32, marginBottom: 12 },
  unavailableText: { fontSize: 13, color: muted, fontFamily: font, lineHeight: 1.7, margin: 0 },
  pastReadOnlyCard: { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 20px' },
  pastReadOnlyIcon: { fontSize: 22, flexShrink: 0 },
  pastReadOnlyText: { fontSize: 12, color: muted, fontFamily: 'Georgia, serif', margin: 0, lineHeight: 1.6, fontStyle: 'italic' },

  // Column headers
  colHeaderRow: {
    display: 'flex', alignItems: 'center', gap: 0,
    marginBottom: 6,
  },
  colH: {
    fontSize: 9, color: muted, fontFamily: font, letterSpacing: 2,
    textTransform: 'uppercase', flex: '0 0 32px',
  },
  listDivider: { borderBottom: '1px solid #E8E0D0', marginBottom: 4 },

  // Wrestler rows
  row: {
    display: 'flex', alignItems: 'center', gap: 0,
    padding: '7px 0', borderBottom: `1px solid #F5F0E8`,
    background: '#fff',
  },
  posNum: {
    width: 32, flexShrink: 0, fontSize: 11, color: muted,
    fontFamily: font, textAlign: 'center',
  },
  rankCol: {
    flex: '0 0 50px', fontSize: 11, color: muted,
    fontFamily: 'Georgia, serif',
  },
  nameCol: {
    flex: 1, fontSize: 13, fontWeight: 700, color: ink,
    fontFamily: font, minWidth: 0,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  metaCol: {
    flex: '0 0 75px', display: 'flex', flexDirection: 'column',
    alignItems: 'flex-end', gap: 1,
  },
  metaItem: {
    fontSize: 10, color: muted, fontFamily: 'Georgia, serif',
    whiteSpace: 'nowrap',
  },

  // Reset button area
  resetArea: { marginTop: 20, paddingTop: 16, borderTop: `1px solid ${cardBorder}` },
  resetBtn: {
    padding: '8px 18px', fontFamily: font, fontSize: 11,
    letterSpacing: 2, textTransform: 'uppercase', cursor: 'pointer',
    background: 'transparent', color: muted,
    border: `1px solid ${cardBorder}`, borderRadius: 2,
  },
  resetConfirm: {
    background: '#FEF9E8', border: `1px solid ${cardBorder}`,
    borderRadius: 4, padding: '14px 16px',
  },
  resetConfirmText: {
    fontSize: 12, color: ink, fontFamily: font, margin: '0 0 12px', lineHeight: 1.5,
  },
  resetConfirmBtns: { display: 'flex', gap: 8 },
  confirmBtn: {
    padding: '8px 18px', fontFamily: font, fontSize: 11,
    letterSpacing: 2, textTransform: 'uppercase', cursor: 'pointer',
    background: accent, color: '#F5F0E8', border: 'none', borderRadius: 2,
  },
  cancelBtn: {
    padding: '8px 18px', fontFamily: font, fontSize: 11,
    letterSpacing: 2, textTransform: 'uppercase', cursor: 'pointer',
    background: 'transparent', color: muted,
    border: `1px solid ${cardBorder}`, borderRadius: 2,
  },
}
